import React from 'react';
import { 
    Card, CardContent, Stack, Box, FormControl, InputLabel, 
    Select, MenuItem, Button, Typography, useTheme 
} from '@mui/material';
import SaveRoundedIcon from '@mui/icons-material/SaveRounded';
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded';
import RestartAltRoundedIcon from '@mui/icons-material/RestartAltRounded';
import SportsScoreRoundedIcon from '@mui/icons-material/SportsScoreRounded';

const LineupControls = ({
    formations,
    selectedFormationId,
    setSelectedFormationId,
    setIsDirty,
    isLocked,
    handleSave,
    setIsManual,
    isSaving,
    setCopyDialogOpen,
    previousLineups,
    handleClearLineup,
    playersOnPitch
}) => {
    const theme = useTheme();

    return (
        <Card sx={{ 
            mb: 4, 
            borderRadius: 8, 
            border: '1px solid', 
            borderColor: 'divider',
            background: 'rgba(255,255,255,0.01)',
            backdropFilter: 'blur(20px)',
            boxShadow: 'none'
        }}>
            <CardContent sx={{ p: 4 }}>
                <Stack spacing={4}>
                    <Box sx={{ display: 'flex', gap: 3, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                        <Box sx={{ flexGrow: 1, minWidth: 280 }}>
                            <Typography variant="caption" sx={{ fontWeight: 900, color: 'primary.main', textTransform: 'uppercase', display: 'block', mb: 1, letterSpacing: '0.05em' }}>
                                Formation
                            </Typography>
                            <FormControl fullWidth sx={{ 
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: 4,
                                    bgcolor: 'rgba(255,255,255,0.03)',
                                    '& fieldset': { borderColor: 'divider' },
                                    '&:hover fieldset': { borderColor: 'primary.main' },
                                }
                            }}>
                                <Select
                                    value={selectedFormationId}
                                    displayEmpty
                                    disabled={isLocked}
                                    onChange={e => {
                                        setSelectedFormationId(e.target.value);
                                        setIsDirty(true);
                                    }}
                                    sx={{ fontWeight: 800 }}
                                >
                                    <MenuItem value="" disabled><em>Formation wählen...</em></MenuItem>
                                    {formations.map(f => (
                                        <MenuItem key={f.id} value={f.id} sx={{ fontWeight: 700 }}>{f.name}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Box>

                        <Box sx={{ minWidth: { xs: '100%', sm: 'auto' } }}>
                            <Typography variant="caption" sx={{ fontWeight: 900, color: 'text.secondary', textTransform: 'uppercase', display: 'block', mb: 1, letterSpacing: '0.05em' }}>
                                Veröffentlichen
                            </Typography>
                            <Button
                                variant="contained"
                                fullWidth
                                startIcon={<SaveRoundedIcon />}
                                onClick={() => { handleSave(); setIsManual(true) }}
                                disabled={!selectedFormationId || isLocked || isSaving}
                                sx={{ 
                                    px: 4, 
                                    height: 56, 
                                    borderRadius: 4, 
                                    fontWeight: 900,
                                    bgcolor: 'primary.main',
                                    color: '#000',
                                    '&:hover': { bgcolor: 'primary.dark' },
                                    '&.Mui-disabled': { bgcolor: 'rgba(255,255,255,0.05)', color: 'rgba(255,255,255,0.2)' }
                                }}
                            >
                                Final Speichern
                            </Button>
                        </Box>
                    </Box>

                    <Box sx={{ 
                        display: 'flex', 
                        gap: 2, 
                        flexWrap: 'wrap', 
                        pt: 3, 
                        borderTop: '1px solid', 
                        borderColor: 'divider' 
                    }}>
                        <Button
                            variant="outlined"
                            startIcon={<ContentCopyRoundedIcon />}
                            onClick={() => setCopyDialogOpen(true)}
                            disabled={isLocked || !previousLineups?.lineups?.length}
                            sx={{ 
                                borderRadius: 3, 
                                fontWeight: 800, 
                                border: '1px solid',
                                borderColor: 'divider',
                                color: 'text.primary',
                                '&:hover': { borderColor: 'primary.main', bgcolor: 'rgba(190, 242, 100, 0.05)' }
                            }}
                        >
                            Vergangene laden
                        </Button>
                        <Button
                            variant="outlined"
                            color="error"
                            startIcon={<RestartAltRoundedIcon />}
                            onClick={handleClearLineup}
                            disabled={isLocked || Object.keys(playersOnPitch).length === 0}
                            sx={{ 
                                borderRadius: 3, 
                                fontWeight: 800,
                                border: '1px solid',
                                '&:hover': { border: '1px solid', bgcolor: 'rgba(211, 47, 47, 0.05)' }
                            }}
                        >
                            Spielfeld leeren
                        </Button>
                        
                        <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 1, color: 'text.secondary' }}>
                            <SportsScoreRoundedIcon fontSize="small" sx={{ opacity: 0.5 }} />
                            <Typography variant="caption" sx={{ fontWeight: 800, textTransform: 'uppercase' }}>
                                {Object.keys(playersOnPitch).length} / 11 Spieler aufgestellt
                            </Typography>
                        </Box>
                    </Box>
                </Stack>
            </CardContent>
        </Card>
    );
};

export default LineupControls;
